import { useState, useCallback, useEffect, useMemo } from 'react';
import { BLOCK_TYPES, BLOCK_COLORS } from './constants';
import {
  buildDayIntervals,
  defaultConfig,
  getDateKey,
  getDefaultRulesForDate,
  loadConfig,
  restoreBackup,
  saveConfig,
  sortIntervals,
  toCalendarEvents,
} from './calendarUtils';

// ─── Local helpers ────────────────────────────────────────────────────────────

const toTimeString = (date) =>
  `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

const asDate   = (v) => (v instanceof Date ? v : new Date(v));

const dateKeyToDate = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const createEventId = () => `event-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const isSameTimeValue = (date, time) => {
  if (!date || !time) return false;
  return toTimeString(date) === time;
};
// ─── Hook ─────────────────────────────────────────────────────────────────────
export const useResourceCalendar = () => {
  const [config,       setConfig      ] = useState(() => loadConfig());
  const [selectedDate, setSelectedDate] = useState(null);

  // Persist on every change
  useEffect(() => { saveConfig(config); }, [config]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const getRuleIntervalsForKey = useCallback(
    (dateKey, defaultRules) => {
      const rules = getDefaultRulesForDate(defaultRules, dateKeyToDate(dateKey));
      return rules.flatMap((rule) => rule.intervals || []);
    },
    [],
  );

  const getOverrideIntervalsForKey = useCallback(
    (dateKey, overrides) => {
      const override = overrides[dateKey];
      return override?.enabled ? [...(override.intervals || [])] : [];
    },
    [],
  );

  // ── addEvent ───────────────────────────────────────────────────────────────

  const addEvent = useCallback((eventData) => {
    const startDate = asDate(eventData.start);
    const endDate   = asDate(eventData.end);
    const dateKey   = getDateKey(startDate);

    const startTime = eventData.allDay ? '09:00' : toTimeString(startDate);
    const endTime   = eventData.allDay ? '17:00' : toTimeString(endDate);

    setConfig((prev) => {
      const existing  = prev.overrides[dateKey] ?? { enabled: true, intervals: [] };
      const intervals = sortIntervals([
        ...(existing.intervals ?? []),
        {
          id: createEventId(),
          title: eventData.title,
          details: eventData.details ?? '',
          start: startTime,
          end: endTime,
          type: eventData.type ?? BLOCK_TYPES.WORK,
        },
      ]);

      return {
        ...prev,
        overrides: {
          ...prev.overrides,
          [dateKey]: { ...existing, enabled: true, intervals },
        },
      };
    });
  }, []);

  // ── updateEvent ────────────────────────────────────────────────────────────
  // Called after drag/resize. extendedProps carries dateKey + intervalIndex
  // that were embedded when the event was first created by toCalendarEvents.

  const updateEvent = useCallback((eventId, updates, meta = {}) => {
    const startDate = asDate(updates.start);
    const endDate   = asDate(updates.end ?? updates.start);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return;

    const sourceDateKey = meta.dateKey ?? getDateKey(startDate);
    const targetDateKey = getDateKey(startDate);
    const itemId        = meta.itemId ?? eventId;
    const intervalIndex = Number(meta.intervalIndex);
    const hasIndex      = !Number.isNaN(intervalIndex);

    const updatedInterval = {
      id:     itemId,
      title:  updates.title ?? meta.title,
      details: updates.details ?? meta.details ?? '',
      start: updates.allDay ? '09:00' : toTimeString(startDate),
      end:   updates.allDay ? '17:00' : toTimeString(endDate),
      type:  updates.type ?? meta.type ?? BLOCK_TYPES.WORK,
    };

    setConfig((prev) => {
      const nextOverrides = { ...prev.overrides };
      const eventSource = meta.source ?? 'override';

      if (eventSource === 'defaultRule') {
        const isNoOpOnRule =
          sourceDateKey === targetDateKey &&
          isSameTimeValue(startDate, meta.originalStart) &&
          isSameTimeValue(endDate, meta.originalEnd) &&
          (updates.title ?? meta.title) === (meta.originalTitle ?? meta.title) &&
          (updates.type ?? meta.type ?? BLOCK_TYPES.WORK) === (meta.originalType ?? meta.type ?? BLOCK_TYPES.WORK) &&
          (updates.details ?? meta.details ?? '') === (meta.details ?? '');

        if (isNoOpOnRule) return prev;

        const targetOverride = nextOverrides[targetDateKey] ?? { enabled: true, intervals: [] };
        nextOverrides[targetDateKey] = {
          ...targetOverride,
          enabled: true,
          intervals: sortIntervals([
            ...(targetOverride.intervals || []),
            { ...updatedInterval, id: createEventId() },
          ]),
        };
        return { ...prev, overrides: nextOverrides };
      }

      const sourceOverride = nextOverrides[sourceDateKey] ?? { enabled: true, intervals: [] };
      const sourceIntervals = getOverrideIntervalsForKey(sourceDateKey, nextOverrides);

      if (sourceDateKey === targetDateKey) {
        nextOverrides[sourceDateKey] = {
          ...sourceOverride,
          enabled: true,
          intervals: sortIntervals(sourceIntervals.map((interval, index) => {
            if (interval.id === itemId || (!interval.id && hasIndex && index === intervalIndex)) {
              return { ...interval, ...updatedInterval };
            }
            return interval;
          })),
        };
        return { ...prev, overrides: nextOverrides };
      }

      nextOverrides[sourceDateKey] = {
        ...sourceOverride,
        enabled: true,
        intervals: sortIntervals(sourceIntervals.filter((interval, index) =>
          !(interval.id === itemId || (!interval.id && hasIndex && index === intervalIndex))
        )),
      };

      const targetOverride = nextOverrides[targetDateKey] ?? { enabled: true, intervals: [] };
      nextOverrides[targetDateKey] = {
        ...targetOverride,
        enabled: true,
        intervals: sortIntervals([
          ...(targetOverride.intervals || []),
          updatedInterval,
        ]),
      };

      return { ...prev, overrides: nextOverrides };
    });
  }, [getOverrideIntervalsForKey]);

  // ── deleteEvent ────────────────────────────────────────────────────────────

  const deleteEvent = useCallback((eventId, meta = {}) => {
    const { dateKey } = meta;
    const itemId = meta.itemId ?? eventId;
    const intervalIndex = Number(meta.intervalIndex);

    if (!dateKey) {
      console.warn('deleteEvent: missing dateKey in extendedProps', meta);
      return;
    }

    if (meta.source === 'defaultRule') {
      return;
    }

    setConfig((prev) => {
      const override = prev.overrides[dateKey];
      if (!override) return prev;

      const intervals = sortIntervals(
        (override.intervals ?? []).filter((interval, idx) => {
          if (interval.id) return interval.id !== itemId;
          return idx !== intervalIndex;
        })
      );

      return {
        ...prev,
        overrides: {
          ...prev.overrides,
          [dateKey]: { ...override, intervals },
        },
      };
    });
  }, []);

  // ── getEventsInRange ───────────────────────────────────────────────────────
  // Fix: clone `day` before passing to toCalendarEvents to avoid mutation aliasing

  const getEventsInRange = useCallback(
    (start, end) => {
      const rangeStart = asDate(start);
      const rangeEnd   = asDate(end);
      const result     = [];

      for (
        let day = new Date(rangeStart);
        day < rangeEnd;
        day.setDate(day.getDate() + 1)
      ) {
        const snapshot  = new Date(day); // ← clone before passing anywhere
        const dateKey   = getDateKey(snapshot);
        const ruleIntervals = getRuleIntervalsForKey(dateKey, config.defaultRules);
        const overrideIntervals = getOverrideIntervalsForKey(dateKey, config.overrides);

        result.push(
          ...toCalendarEvents(snapshot, ruleIntervals, dateKey, {
            source: 'defaultRule',
            editable: true,
            idPrefix: 'rule:',
          }).map((ev) => ({
            ...ev,
            backgroundColor: BLOCK_COLORS[ev.type],
            borderColor:     BLOCK_COLORS[ev.type],
            editable:        true,
          })),
          ...toCalendarEvents(snapshot, overrideIntervals, dateKey, {
            source: 'override',
            editable: true,
            idPrefix: 'override:',
          }).map((ev) => ({
            ...ev,
            backgroundColor: BLOCK_COLORS[ev.type],
            borderColor:     BLOCK_COLORS[ev.type],
            editable:        true,
          })),
        );
      }

      return result;
    },
    [config, getOverrideIntervalsForKey, getRuleIntervalsForKey],
  );

  // ── Derived state ──────────────────────────────────────────────────────────

  const events = useMemo(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(),     1);
    const end   = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return getEventsInRange(start, end);
  }, [getEventsInRange]);

  const getEventsByType = useCallback(
    (type) => events.filter((ev) => ev.type === type),
    [events],
  );

  const normalizedOutput = useMemo(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(),     1);
    // Last day of month: month+1, day 0 rolls back to last day of current month
    const end   = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const output = [];

    for (
      let day = new Date(start);
      day <= end;
      day.setDate(day.getDate() + 1)
    ) {
      const snapshot  = new Date(day);
      const dateKey   = getDateKey(snapshot);
      const override  = config.overrides[dateKey];
      const rules     = getDefaultRulesForDate(config.defaultRules, snapshot);
      const intervals = buildDayIntervals(rules, override);

      intervals.forEach((iv) => {
        output.push({ date: dateKey, start: iv.start, end: iv.end, type: iv.type });
      });
    }

    return output;
  }, [config]);

  // ── Config mutations ───────────────────────────────────────────────────────

  const clearAllEvents = useCallback(() => setConfig(defaultConfig), []);

  const updateDefaultRule = useCallback((ruleId, updates) => {
    setConfig((prev) => ({
      ...prev,
      defaultRules: prev.defaultRules.map((r) =>
        r.id === ruleId ? { ...r, ...updates } : r
      ),
    }));
  }, []);

  const addDefaultRule = useCallback(() => {
    setConfig((prev) => ({
      ...prev,
      defaultRules: [
        ...prev.defaultRules,
        {
          id:         `rule-${Date.now()}`,
          name:       'New Rule',
          daysOfWeek: [1, 2, 3, 4, 5],
          intervals:  [{ start: '09:00', end: '17:00', type: BLOCK_TYPES.WORK }],
        },
      ],
    }));
  }, []);

  const removeDefaultRule = useCallback((ruleId) => {
    setConfig((prev) => ({
      ...prev,
      defaultRules: prev.defaultRules.filter((r) => r.id !== ruleId),
    }));
  }, []);

  const updateOverride = useCallback((dateKey, override) => {
    setConfig((prev) => ({
      ...prev,
      overrides: { ...prev.overrides, [dateKey]: override },
    }));
  }, []);

  const selectDate = useCallback((date) => setSelectedDate(date), []);

  const restoreFromBackup = useCallback(() => {
    const backup = restoreBackup();
    if (backup) setConfig(backup);
  }, []);

  return {
    config,
    events,
    getEventsInRange,
    selectedDate,
    normalizedOutput,
    addEvent,
    updateEvent,
    deleteEvent,
    getEventsByType,
    clearAllEvents,
    updateDefaultRule,
    addDefaultRule,
    removeDefaultRule,
    updateOverride,
    selectDate,
    restoreFromBackup,
  };
};
